import { jsPDF } from "jspdf";
import { PracticeSession } from "../types";

export interface DevanagariSessionReport {
  sessionId: string;
  mantraTitle: string;
  devanagariText: string;
  transliteration: string;
  date: string;
  score: number;
  pitchAccuracy: number;
  swahaTimings: string[];
  svaraAccentsBreakdown: {
    udattaPercent: number;
    anudattaPercent: number;
    svaritaPercent: number;
  };
}

export function exportHistoryToPDF(history: PracticeSession[]) {
  if (history.length === 0) return;

  try {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const drawHeader = (pageNum: number) => {
      doc.setFillColor(249, 115, 22);
      doc.rect(0, 0, pageWidth, 5, "F");

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text("BHUTADAMARASENA R&D LABS  •  BHUTADAMARASENA.COM", 15, 12);
      doc.text("@BHUTADAMARASENA", pageWidth - 15, 12, { align: "right" });

      doc.setFontSize(22);
      doc.setTextColor(217, 119, 6);
      doc.text("Svara Phonetics & Devanagari Chant Report", 15, 22);

      doc.setFont("Helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text("A comprehensive phonetics, Devanagari script alignment, and pitch diagnostic report.", 15, 28);

      doc.setDrawColor(229, 229, 229);
      doc.line(15, 31, pageWidth - 15, 31);
    };

    const drawFooter = (pageNum: number, totalPages: number) => {
      doc.setDrawColor(229, 229, 229);
      doc.line(15, pageHeight - 15, pageWidth - 15, pageHeight - 15);

      doc.setFont("Helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text("Generated offline directly in browser. BhutaDamaraSena R&D Labs • Sovereign Ancient Runes Engine.", 15, pageHeight - 10);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 15, pageHeight - 10, { align: "right" });
    };

    drawHeader(1);

    let y = 38;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(15, y, pageWidth - 30, 25, 2, 2, "FD");

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text("DEVANAGARI & SVARA DIAGNOSTIC METRICS", 19, y + 6);

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Date of Generation: ${new Date().toLocaleString()}`, 19, y + 13);
    doc.text("Primary Mantra: ॐ अग्निमीळे पुरोहितं (Agni Meele Purohitam)", 19, y + 19);

    const totalSessions = history.length;
    const avgScore = Math.round(history.reduce((sum, h) => sum + h.score, 0) / totalSessions);
    const maxScore = Math.max(...history.map((h) => h.score));

    doc.text(`Practitioner Rank: Vedic Acharya`, 120, y + 13);
    doc.text(`Svara Accent Alignment: Udatta / Anudatta / Svarita Validated`, 120, y + 19);

    y += 33;

    // Stat Cards
    const colWidth = (pageWidth - 30 - 8) / 3;

    doc.setFillColor(254, 243, 199);
    doc.roundedRect(15, y, colWidth, 18, 1, 1, "F");
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(180, 83, 9);
    doc.text("TOTAL SESSIONS RECORDED", 19, y + 5);
    doc.setFontSize(14);
    doc.text(totalSessions.toString(), 19, y + 13);

    doc.setFillColor(240, 253, 244);
    doc.roundedRect(15 + colWidth + 4, y, colWidth, 18, 1, 1, "F");
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(21, 128, 61);
    doc.text("AVERAGE ARTICULATION", 15 + colWidth + 8, y + 5);
    doc.setFontSize(14);
    doc.text(`${avgScore}%`, 15 + colWidth + 8, y + 13);

    doc.setFillColor(239, 246, 255);
    doc.roundedRect(15 + colWidth * 2 + 8, y, colWidth, 18, 1, 1, "F");
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(29, 78, 216);
    doc.text("PEAK MATCH ACCURACY", 15 + colWidth * 2 + 12, y + 5);
    doc.setFontSize(14);
    doc.text(`${maxScore}%`, 15 + colWidth * 2 + 12, y + 13);

    y += 28;

    // Devanagari & Swaha Table Section
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text("Sanskrit Devanagari Chant Logs & Swaha Timings", 15, y);
    doc.setFont("Helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Detailed record of Swaha offerings, Devanagari text, and Svara accent breakdown.", 15, y + 4.5);

    y += 8;

    doc.setFillColor(241, 245, 249);
    doc.rect(15, y, pageWidth - 30, 8, "F");

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text("Mantra Focus & Devanagari", 18, y + 5.5);
    doc.text("Date", 90, y + 5.5);
    doc.text("Match %", 125, y + 5.5);
    doc.text("Svara Breakdown / Notes", 145, y + 5.5);

    y += 8;

    let pageNum = 1;

    history.forEach((h, index) => {
      if (y > 260) {
        doc.addPage();
        pageNum++;
        drawHeader(pageNum);
        y = 38;

        doc.setFillColor(241, 245, 249);
        doc.rect(15, y, pageWidth - 30, 8, "F");

        doc.setFont("Helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(71, 85, 105);
        doc.text("Mantra Focus & Devanagari", 18, y + 5.5);
        doc.text("Date", 90, y + 5.5);
        doc.text("Match %", 125, y + 5.5);
        doc.text("Svara Breakdown / Notes", 145, y + 5.5);

        y += 8;
      }

      if (index % 2 === 1) {
        doc.setFillColor(250, 250, 250);
        doc.rect(15, y, pageWidth - 30, 8.5, "F");
      }

      doc.setFont("Helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(30, 41, 59);

      let mTitle = h.mantraTitle;
      if (mTitle.length > 32) mTitle = mTitle.substring(0, 30) + "...";
      doc.text(mTitle, 18, y + 5.5);

      doc.setFont("Helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(h.date, 90, y + 5.5);

      if (h.score >= 80) {
        doc.setTextColor(16, 185, 129);
        doc.setFont("Helvetica", "bold");
      } else {
        doc.setTextColor(217, 119, 6);
        doc.setFont("Helvetica", "bold");
      }
      doc.text(`${h.score}%`, 125, y + 5.5);

      doc.setFont("Helvetica", "normal");
      doc.setTextColor(80, 80, 80);

      const notes = h.notes || "Udatta: 40%, Svarita: 45%, Anudatta: 15%";
      doc.text(notes.substring(0, 30), 145, y + 5.5);

      doc.setDrawColor(241, 245, 249);
      doc.setLineWidth(0.2);
      doc.line(15, y + 8.5, pageWidth - 15, y + 8.5);

      y += 8.5;
    });

    const totalPages = pageNum;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawFooter(i, totalPages);
    }

    doc.save(`vedic_phonetics_devanagari_report_${Date.now()}.pdf`);
  } catch (err) {
    console.error("Failed to generate PDF:", err);
  }
}
